import { useState, useEffect } from "react";
import {
  fetchAccountingList, createAccounting,
  updateAccounting, deleteAccounting
} from "../api/accountingApi";
import Toast from "../components/Toast";
import { getCodeList, getCodeNm } from "../api/codeApi";

// ===================== 등록/수정 모달 =====================
const FormModal = ({ data, onClose, onSave }) => {
  const accTypeList = getCodeList('ACC_TYPE'); // ← 추가
  const isNew = !data;
  const [form, setForm] = useState(data || {
    acc_dt: new Date().toISOString().slice(0, 10),
    acc_type: accTypeList[0]?.code || 'I', // ← 첫 번째 코드로 초기값
    acc_content: '', acc_amt: '', acc_remark: ''
  });
  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const handleSave = () => {
    if (!form.acc_dt || !form.acc_content || !form.acc_amt) {
      alert("처리일자, 내용, 금액은 필수입니다.");
      return;
    }
    onSave(form);
  };

  return (
    <div style={ms.overlay}>
      <div style={ms.modal}>
        <div style={ms.header}>
          <span style={ms.title}>{isNew ? '💰 회계 등록' : '✏️ 회계 수정'}</span>
          <button style={ms.closeBtn} onClick={onClose}>✕</button>
        </div>
        <div style={ms.body}>
          <div style={{ display: "flex", gap: 12 }}>
            <div style={{ ...ms.field, flex: 1 }}>
              <label style={ms.label}>처리일자 <span style={{ color: "#ff4d4f" }}>*</span></label>
              <input style={ms.input} type="date" value={form.acc_dt}
                onChange={e => set('acc_dt', e.target.value)} />
            </div>
            <div style={{ ...ms.field, flex: 1 }}>
              <label style={ms.label}>구분 <span style={{ color: "#ff4d4f" }}>*</span></label>
              <select style={ms.input} value={form.acc_type} onChange={e => set('acc_type', e.target.value)}>
                {accTypeList.map(c => (
                  <option key={c.code} value={c.code}>{c.code_nm}</option>
                ))}
              </select>
            </div>
          </div>
          <div style={ms.field}>
            <label style={ms.label}>내용 <span style={{ color: "#ff4d4f" }}>*</span></label>
            <input style={ms.input} placeholder="내용을 입력하세요" value={form.acc_content}
              onChange={e => set('acc_content', e.target.value)} />
          </div>
          <div style={ms.field}>
            <label style={ms.label}>금액 <span style={{ color: "#ff4d4f" }}>*</span></label>
            <input style={ms.input} type="number" placeholder="금액 입력" value={form.acc_amt}
              onChange={e => set('acc_amt', e.target.value)} />
          </div>
          <div style={ms.field}>
            <label style={ms.label}>비고</label>
            <input style={ms.input} placeholder="비고 (선택)" value={form.acc_remark || ''}
              onChange={e => set('acc_remark', e.target.value)} />
          </div>
        </div>
        <div style={ms.footer}>
          <button style={ms.cancelBtn} onClick={onClose}>취소</button>
          <button style={ms.saveBtn} onClick={handleSave}>{isNew ? '등록' : '저장'}</button>
        </div>
      </div>
    </div>
  );
};

// ===================== 메인 페이지 =====================
export default function AccountingPage({ onLogout }) {
  const accTypeList = getCodeList('ACC_TYPE'); // ← 추가
  const incomeCode  = accTypeList.find(c => c.code === 'I')?.code || 'I';
  const expenseCode = accTypeList.find(c => c.code === 'O')?.code || 'O';
  const [list,        setList]        = useState([]);
  const [loading,     setLoading]     = useState(false);
  const [toast,       setToast]       = useState('');
  const [yearFilter,  setYearFilter]  = useState(new Date().getFullYear().toString());
  const [monthFilter, setMonthFilter] = useState('-');
  const [showForm,    setShowForm]    = useState(false);
  const [editData,    setEditData]    = useState(null);
  const [yearList, setYearList] = useState([]);

  const showToast = (msg) => { setToast(msg); setTimeout(() => setToast(''), 2500); };

  const loadList = async (year) => {
    setLoading(true);
    try {
      const res = await fetchAccountingList(year);
      if (res.success) setList(res.data);
      else if (res.message === '토큰이 만료되었습니다. 다시 로그인해주세요.') onLogout();
    } catch (err) { showToast('❌ 목록 조회 실패'); }
    finally { setLoading(false); }
  };

  useEffect(() => { loadList(yearFilter); }, [yearFilter]);
  useEffect(() => {
    setYearList(getCodeList('YEAR_LIST'));
  }, []);

  // 월 필터링
  const filteredList = monthFilter === '-'
    ? list
    : list.filter(r => r.acc_dt && r.acc_dt.slice(5, 7) === monthFilter);

  const incomeList  = filteredList.filter(r => r.acc_type === incomeCode);
  const expenseList = filteredList.filter(r => r.acc_type === expenseCode);
  const totalIncome  = incomeList.reduce((s, r)  => s + Number(r.acc_amt), 0);
  const totalExpense = expenseList.reduce((s, r) => s + Number(r.acc_amt), 0);
  const balance      = totalIncome - totalExpense;

  const handleSave = async (form) => {
    try {
      const res = editData
        ? await updateAccounting(editData.acc_year, editData.seq_no, form)
        : await createAccounting(form);
      if (res.success) {
        setShowForm(false); setEditData(null);
        loadList(yearFilter);
        showToast(editData ? '✅ 수정되었습니다.' : '✅ 등록되었습니다.');
      } else showToast(`❌ ${res.message}`);
    } catch (err) { showToast('❌ 저장 실패'); }
  };

  const handleDelete = async (item) => {
    if (!window.confirm(`"${item.acc_content}" 을 삭제하시겠습니까?`)) return;
    try {
      const res = await deleteAccounting(item.acc_year, item.seq_no);
      if (res.success) { loadList(yearFilter); showToast('✅ 삭제되었습니다.'); }
      else showToast(`❌ ${res.message}`);
    } catch (err) { showToast('❌ 삭제 실패'); }
  };

  const handleExcelDownload = () => {
    import("xlsx").then((XLSX) => {
      const headers = ["연번", "처리일자", "구분", "내용", "금액", "비고"];
      const rows = filteredList.map(r => [
        r.seq_no, r.acc_dt,
        getCodeNm('ACC_TYPE', r.acc_type),
        r.acc_content, Number(r.acc_amt), r.acc_remark || ''
      ]);
      rows.push([]);
      rows.push(['', '', '수입합계', '', totalIncome,  '']);
      rows.push(['', '', '지출합계', '', totalExpense, '']);
      rows.push(['', '', '잔액',     '', balance,      '']);
      const ws = XLSX.utils.aoa_to_sheet([headers, ...rows]);
      ws['!cols'] = [{ wch: 6 }, { wch: 14 }, { wch: 8 }, { wch: 30 }, { wch: 14 }, { wch: 20 }];
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, `${yearFilter}년 회계`);
      XLSX.writeFile(wb, `간편회계_${yearFilter}${monthFilter !== '-' ? '_' + monthFilter + '월' : ''}.xlsx`);
    });
  };

  const handlePrint = () => {
    const periodLabel = monthFilter !== '-'
      ? `${yearFilter}년 ${monthFilter}월`
      : `${yearFilter}년`;

    const incomeRows = incomeList.map((r, i) => `
      <tr>
        <td>${i + 1}</td>
        <td>${r.acc_dt}</td>
        <td>${r.acc_content}</td>
        <td style="text-align:right">${Number(r.acc_amt).toLocaleString()}</td>
        <td>${r.acc_remark || ''}</td>
      </tr>`).join('');

    const expenseRows = expenseList.map((r, i) => `
      <tr>
        <td>${i + 1}</td>
        <td>${r.acc_dt}</td>
        <td>${r.acc_content}</td>
        <td style="text-align:right">${Number(r.acc_amt).toLocaleString()}</td>
        <td>${r.acc_remark || ''}</td>
      </tr>`).join('');

    const printHtml = `<!DOCTYPE html>
    <html lang="ko">
    <head>
    <meta charset="utf-8"/>
    <title>회계결산서</title>
    <style>
      * { margin: 0; padding: 0; box-sizing: border-box; }
      body { font-family: '맑은 고딕','Malgun Gothic',sans-serif; padding: 40px 50px; font-size: 13px; color: #000; }
      h1 { text-align: center; font-size: 20px; font-weight: bold; letter-spacing: 8px; margin-bottom: 6px; }
      .period { text-align: center; font-size: 14px; margin-bottom: 24px; color: #444; }
      .tables { display: grid; grid-template-columns: 1fr 1fr; gap: 20px; }
      .section-title { font-size: 14px; font-weight: bold; padding: 6px 0; border-bottom: 2px solid #000; margin-bottom: 4px; }
      .income-title { color: #1677ff; border-color: #1677ff; }
      .expense-title { color: #cc0000; border-color: #cc0000; }
      table { width: 100%; border-collapse: collapse; font-size: 12px; }
      th { background: #f5f5f5; padding: 6px 8px; border: 1px solid #ccc; text-align: center; font-weight: bold; }
      td { padding: 5px 8px; border: 1px solid #ddd; }
      .total-row td { background: #f0f0f0; font-weight: bold; }
      .summary { margin-top: 24px; border-top: 2px solid #000; padding-top: 12px; display: flex; justify-content: flex-end; gap: 40px; font-size: 14px; }
      .summary-item { display: flex; flex-direction: column; align-items: flex-end; gap: 4px; }
      .summary-label { font-weight: bold; color: #666; font-size: 12px; }
      .summary-value { font-size: 16px; font-weight: bold; }
      @media print { @page { size: A4 landscape; margin: 15mm; } }
    </style>
    </head>
    <body>
      <h1>회 계 결 산 서</h1>
      <div class="period">${periodLabel}</div>
      <div class="tables">
        <div>
          <div class="section-title income-title">📥 수입</div>
          <table>
            <thead>
              <tr><th>No</th><th>일자</th><th>내용</th><th>금액</th><th>비고</th></tr>
            </thead>
            <tbody>
              ${incomeRows || '<tr><td colspan="5" style="text-align:center;color:#aaa">내역 없음</td></tr>'}
              <tr class="total-row">
                <td colspan="3" style="text-align:right">수입 합계</td>
                <td style="text-align:right">${totalIncome.toLocaleString()}</td>
                <td></td>
              </tr>
            </tbody>
          </table>
        </div>
        <div>
          <div class="section-title expense-title">📤 지출</div>
          <table>
            <thead>
              <tr><th>No</th><th>일자</th><th>내용</th><th>금액</th><th>비고</th></tr>
            </thead>
            <tbody>
              ${expenseRows || '<tr><td colspan="5" style="text-align:center;color:#aaa">내역 없음</td></tr>'}
              <tr class="total-row">
                <td colspan="3" style="text-align:right">지출 합계</td>
                <td style="text-align:right">${totalExpense.toLocaleString()}</td>
                <td></td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
      <div class="summary">
        <div class="summary-item">
          <span class="summary-label">수입 합계</span>
          <span class="summary-value" style="color:#1677ff">${totalIncome.toLocaleString()} 원</span>
        </div>
        <div class="summary-item">
          <span class="summary-label">지출 합계</span>
          <span class="summary-value" style="color:#cc0000">${totalExpense.toLocaleString()} 원</span>
        </div>
        <div class="summary-item">
          <span class="summary-label">잔액</span>
          <span class="summary-value" style="color:${balance >= 0 ? '#52c41a' : '#fa8c16'}">${balance.toLocaleString()} 원</span>
        </div>
      </div>
    </body>
    </html>`;

    const iframe = document.createElement('iframe');
    iframe.style.cssText = 'position:fixed;width:0;height:0;border:0;visibility:hidden;';
    document.body.appendChild(iframe);
    iframe.contentDocument.open();
    iframe.contentDocument.write(printHtml);
    iframe.contentDocument.close();
    setTimeout(() => {
      iframe.contentWindow.focus();
      iframe.contentWindow.print();
      setTimeout(() => document.body.removeChild(iframe), 1000);
    }, 500);
  };

  return (
    <div style={styles.wrap}>
      <Toast msg={toast} />

      <div style={styles.pageHeader}>
        <h1 style={styles.pageTitle}>💰 간편 회계</h1>
        <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
          {/* 연도 */}
          <select style={styles.select} value={yearFilter}
            onChange={e => setYearFilter(e.target.value)}>
            <option value="">전체 연도</option>
            {yearList.map(y => (
              <option key={y.code} value={y.code}>{y.code_nm}</option>
            ))}
          </select>
          {/* 월 */}
          <select style={styles.select} value={monthFilter}
            onChange={e => setMonthFilter(e.target.value)}>
            <option value="-">전체</option>
            {Array.from({ length: 12 }, (_, i) => String(i + 1).padStart(2, '0')).map(m => (
              <option key={m} value={m}>{parseInt(m)}월</option>
            ))}
          </select>
          <button style={styles.excelBtn}  onClick={handleExcelDownload}>📊 엑셀</button>
          <button style={styles.printBtn}  onClick={handlePrint}>🖨️ 출력</button>
          <button style={styles.registerBtn} onClick={() => { setEditData(null); setShowForm(true); }}>＋ 등록</button>
        </div>
      </div>

      {/* 요약 카드 */}
      <div style={styles.summaryWrap}>
        <div style={{ ...styles.summaryCard, borderColor: "#1677ff" }}>
          <span style={styles.summaryLabel}>수입 합계</span>
          <span style={{ ...styles.summaryAmt, color: "#1677ff" }}>{totalIncome.toLocaleString()} 원</span>
        </div>
        <div style={{ ...styles.summaryCard, borderColor: "#ff4d4f" }}>
          <span style={styles.summaryLabel}>지출 합계</span>
          <span style={{ ...styles.summaryAmt, color: "#ff4d4f" }}>{totalExpense.toLocaleString()} 원</span>
        </div>
        <div style={{ ...styles.summaryCard, borderColor: balance >= 0 ? "#52c41a" : "#fa8c16" }}>
          <span style={styles.summaryLabel}>잔액</span>
          <span style={{ ...styles.summaryAmt, color: balance >= 0 ? "#52c41a" : "#fa8c16" }}>
            {balance.toLocaleString()} 원
          </span>
        </div>
      </div>

      {/* 좌우 분할 리스트 */}
      {loading ? (
        <div style={styles.empty}>⏳ 불러오는 중...</div>
      ) : (
        <div style={styles.tableWrap}>
          {/* 수입 */}
          <div style={styles.tableSection}>
            <div style={{ ...styles.tableTitle, color: "#1677ff", borderColor: "#1677ff" }}>📥 수입</div>
            <div style={styles.card}>
              <div style={{ ...styles.tableHeader, gridTemplateColumns: "1fr 2fr 1.5fr 0.8fr" }}>
                {["일자", "내용", "금액", ""].map(h => <div key={h} style={styles.th}>{h}</div>)}
              </div>
              {incomeList.length === 0 ? (
                <div style={styles.empty}>수입 내역이 없습니다.</div>
              ) : incomeList.map((item, i) => (
                <div key={i} style={{ ...styles.tableRow, gridTemplateColumns: "1fr 2fr 1.5fr 0.8fr" }}>
                  <div style={styles.td}>{item.acc_dt}</div>
                  <div style={styles.td}>
                    <div>
                      <div style={{ fontWeight: 600 }}>{item.acc_content}</div>
                      {item.acc_remark && <div style={{ fontSize: 11, color: "#aaa" }}>{item.acc_remark}</div>}
                    </div>
                  </div>
                  <div style={{ ...styles.td, color: "#1677ff", fontWeight: 600 }}>
                    {Number(item.acc_amt).toLocaleString()}
                  </div>
                  <div style={{ ...styles.td, gap: 4 }}>
                    <button style={styles.smEditBtn} onClick={() => { setEditData(item); setShowForm(true); }}>수정</button>
                    <button style={styles.smDelBtn}  onClick={() => handleDelete(item)}>삭제</button>
                  </div>
                </div>
              ))}
              <div style={{ ...styles.tableRow, gridTemplateColumns: "1fr 2fr 1.5fr 0.8fr", background: "#e8f4fd", fontWeight: 700 }}>
                <div style={styles.td}></div>
                <div style={{ ...styles.td, color: "#1677ff" }}>수입 합계</div>
                <div style={{ ...styles.td, color: "#1677ff" }}>{totalIncome.toLocaleString()}</div>
                <div style={styles.td}></div>
              </div>
            </div>
          </div>

          {/* 지출 */}
          <div style={styles.tableSection}>
            <div style={{ ...styles.tableTitle, color: "#ff4d4f", borderColor: "#ff4d4f" }}>📤 지출</div>
            <div style={styles.card}>
              <div style={{ ...styles.tableHeader, gridTemplateColumns: "1fr 2fr 1.5fr 0.8fr" }}>
                {["일자", "내용", "금액", ""].map(h => <div key={h} style={styles.th}>{h}</div>)}
              </div>
              {expenseList.length === 0 ? (
                <div style={styles.empty}>지출 내역이 없습니다.</div>
              ) : expenseList.map((item, i) => (
                <div key={i} style={{ ...styles.tableRow, gridTemplateColumns: "1fr 2fr 1.5fr 0.8fr" }}>
                  <div style={styles.td}>{item.acc_dt}</div>
                  <div style={styles.td}>
                    <div>
                      <div style={{ fontWeight: 600 }}>{item.acc_content}</div>
                      {item.acc_remark && <div style={{ fontSize: 11, color: "#aaa" }}>{item.acc_remark}</div>}
                    </div>
                  </div>
                  <div style={{ ...styles.td, color: "#ff4d4f", fontWeight: 600 }}>
                    {Number(item.acc_amt).toLocaleString()}
                  </div>
                  <div style={{ ...styles.td, gap: 4 }}>
                    <button style={styles.smEditBtn} onClick={() => { setEditData(item); setShowForm(true); }}>수정</button>
                    <button style={styles.smDelBtn}  onClick={() => handleDelete(item)}>삭제</button>
                  </div>
                </div>
              ))}
              <div style={{ ...styles.tableRow, gridTemplateColumns: "1fr 2fr 1.5fr 0.8fr", background: "#fff2f0", fontWeight: 700 }}>
                <div style={styles.td}></div>
                <div style={{ ...styles.td, color: "#ff4d4f" }}>지출 합계</div>
                <div style={{ ...styles.td, color: "#ff4d4f" }}>{totalExpense.toLocaleString()}</div>
                <div style={styles.td}></div>
              </div>
            </div>
          </div>
        </div>
      )}

      {showForm && (
        <FormModal
          data={editData}
          onClose={() => { setShowForm(false); setEditData(null); }}
          onSave={handleSave}
        />
      )}
    </div>
  );
}

const styles = {
  wrap        : { background: "#f5f7fa", padding: 24, fontFamily: "'Noto Sans KR', sans-serif" },
  pageHeader  : { display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20, flexWrap: "wrap", gap: 12 },
  pageTitle   : { fontSize: 22, fontWeight: 800, color: "#1a1a2e", margin: 0 },
  select      : { border: "1.5px solid #e0e0e0", borderRadius: 8, padding: "8px 12px", fontSize: 14, outline: "none", background: "#fff", cursor: "pointer" },
  excelBtn    : { background: "#fff", border: "1.5px solid #52c41a", color: "#52c41a", borderRadius: 8, padding: "8px 14px", fontSize: 14, fontWeight: 600, cursor: "pointer" },
  printBtn    : { background: "#fff", border: "1.5px solid #fa8c16", color: "#fa8c16", borderRadius: 8, padding: "8px 14px", fontSize: 14, fontWeight: 600, cursor: "pointer" },
  registerBtn : { background: "linear-gradient(135deg, #1677ff, #4096ff)", color: "#fff", border: "none", borderRadius: 8, padding: "8px 16px", fontSize: 14, fontWeight: 700, cursor: "pointer" },
  summaryWrap : { display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12, marginBottom: 20 },
  summaryCard : { background: "#fff", borderRadius: 12, padding: "16px 20px", borderLeft: "4px solid", boxShadow: "0 2px 8px rgba(0,0,0,0.06)", display: "flex", justifyContent: "space-between", alignItems: "center" },
  summaryLabel: { fontSize: 13, fontWeight: 600, color: "#666" },
  summaryAmt  : { fontSize: 18, fontWeight: 800 },
  tableWrap   : { display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 },
  tableSection: { display: "flex", flexDirection: "column", gap: 8 },
  tableTitle  : { fontSize: 15, fontWeight: 700, borderLeft: "3px solid", paddingLeft: 10 },
  card        : { background: "#fff", borderRadius: 12, boxShadow: "0 2px 8px rgba(0,0,0,0.06)", overflow: "hidden" },
  tableHeader : { display: "grid", padding: "12px 16px", background: "#f8faff", borderBottom: "1px solid #e8f0fe" },
  th          : { fontSize: 12, fontWeight: 700, color: "#888" },
  tableRow    : { display: "grid", padding: "12px 16px", borderBottom: "1px solid #f5f5f5", alignItems: "center" },
  td          : { fontSize: 13, color: "#1a1a2e", display: "flex", alignItems: "center" },
  empty       : { textAlign: "center", padding: "40px 0", color: "#bbb", fontSize: 14 },
  smEditBtn   : { background: "#fff", border: "1px solid #1677ff", color: "#1677ff", borderRadius: 5, padding: "3px 8px", fontSize: 11, cursor: "pointer" },
  smDelBtn    : { background: "#fff2f0", border: "1px solid #ffccc7", color: "#ff4d4f", borderRadius: 5, padding: "3px 8px", fontSize: 11, cursor: "pointer" },
};

const ms = {
  overlay  : { position: "fixed", inset: 0, background: "rgba(0,0,0,0.45)", zIndex: 100, display: "flex", alignItems: "center", justifyContent: "center" },
  modal    : { background: "#fff", borderRadius: 16, width: "100%", maxWidth: 440, margin: "0 16px", boxShadow: "0 20px 60px rgba(0,0,0,0.3)", overflow: "hidden" },
  header   : { display: "flex", justifyContent: "space-between", alignItems: "center", padding: "20px 24px", borderBottom: "1px solid #f0f0f0" },
  title    : { fontSize: 16, fontWeight: 700, color: "#1a1a2e" },
  closeBtn : { background: "none", border: "none", fontSize: 18, color: "#aaa", cursor: "pointer" },
  body     : { padding: "20px 24px", display: "flex", flexDirection: "column", gap: 14 },
  field    : { display: "flex", flexDirection: "column", gap: 6 },
  label    : { fontSize: 13, fontWeight: 600, color: "#444" },
  input    : { border: "1.5px solid #e0e0e0", borderRadius: 8, padding: "10px 12px", fontSize: 14, outline: "none", background: "#fafafa", color: "#1a1a2e" },
  footer   : { display: "flex", gap: 10, justifyContent: "flex-end", padding: "16px 24px", borderTop: "1px solid #f0f0f0" },
  cancelBtn: { background: "#fff", border: "1px solid #e0e0e0", borderRadius: 8, padding: "10px 20px", fontSize: 14, color: "#666", cursor: "pointer" },
  saveBtn  : { background: "linear-gradient(135deg, #1677ff, #4096ff)", border: "none", borderRadius: 8, padding: "10px 20px", fontSize: 14, color: "#fff", fontWeight: 700, cursor: "pointer" },
};